import { memo, useCallback, ChangeEvent } from 'react'
import { useTranslation } from 'services/Translation'

import texts from './texts'
import { Period, TimeFilterValue, isCustomRangeValid } from './helpers'
import { FilterBar, Field, Select, DateInput, Notice } from './Styles'

interface TimeFilterProps {
  value: TimeFilterValue
  onChange: (next: TimeFilterValue) => void
}

// Shared Day / Week / Month / Custom Range control used by all analytics tabs
// (Requirement 10.1). When Custom Range is selected, start + end date pickers
// are shown and an inline validation message is rendered if the range is
// incomplete or inverted.
const TimeFilter = ({ value, onChange }: TimeFilterProps) => {
  const { t } = useTranslation(texts)

  const handlePeriod = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...value, period: e.target.value as Period })
    },
    [onChange, value]
  )

  const handleStart = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      onChange({ ...value, start: raw ? new Date(raw).toISOString() : undefined })
    },
    [onChange, value]
  )

  const handleEnd = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      onChange({ ...value, end: raw ? new Date(raw).toISOString() : undefined })
    },
    [onChange, value]
  )

  // Convert an ISO timestamp into a yyyy-mm-dd value for the date input.
  const toDateInput = (iso?: string): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toISOString().slice(0, 10)
  }

  const rangeValid = isCustomRangeValid(value)

  return (
    <div>
      <FilterBar>
        <Field>
          {t('period')}
          <Select value={value.period} onChange={handlePeriod} data-cy="walletPeriodSelect">
            <option value="day">{t('day')}</option>
            <option value="week">{t('week')}</option>
            <option value="month">{t('month')}</option>
            <option value="custom_range">{t('customRange')}</option>
          </Select>
        </Field>

        {value.period === 'custom_range' && (
          <>
            <Field>
              {t('startDate')}
              <DateInput
                type="date"
                value={toDateInput(value.start)}
                onChange={handleStart}
                data-cy="walletStartDate"
              />
            </Field>
            <Field>
              {t('endDate')}
              <DateInput
                type="date"
                value={toDateInput(value.end)}
                onChange={handleEnd}
                data-cy="walletEndDate"
              />
            </Field>
          </>
        )}
      </FilterBar>

      {value.period === 'custom_range' && !rangeValid && (
        <Notice variant="warning">{t('invalidRange')}</Notice>
      )}
    </div>
  )
}

TimeFilter.whyDidYouRender = true

export default memo(TimeFilter)
